/**
 * The FILENAME and TITLE of an order PDF — the two most outward-facing strings
 * this app produces, because they are what a supplier receives.
 *
 * 🔀 **EXTRACTED FROM `components/OrderPdfButton.js` ON 2026-08-10** so the
 * order actions menu's own Download / Print / Share items can produce exactly
 * the same document identity as the `<OrderPdfButton>` dropdown they replace on
 * the order list pages. Two copies of this logic is precisely the shape that
 * produced the `PO-PO-147` defect below — one copy, imported twice.
 * `OrderPdfButton` now imports these and is otherwise unchanged; its props out
 * are byte-identical, which `components/OrderPdfButton.test.js` pins on both
 * branches.
 *
 * 🔀 **BOTH STRINGS STOPPED HARD-CODING A `PO` PREFIX ON 2026-08-08**, because
 * the stored value now carries one (`server/helpers/poNumber.js`; product
 * owner: *"Make all Po numbers start with PO-"*). Left alone, a supplier would
 * have received `PO-PO-147.pdf` titled `Order PO:PO-147` — the owner's own
 * instruction doubled.
 *
 * ⚠️ The legacy branch is kept because the backfill is a one-off an operator
 * runs, so a row it has not reached (or one restored from an old archive on an
 * instance where the restore-path normalisation was removed) can still hold a
 * bare `147`. Those still get the label, exactly as they did before. The test
 * mirrors `getOrderTitle`'s: separator required, so `POWER-1` is not mistaken
 * for a prefixed value.
 */

import { getOrderNumber } from './orderTitle';

/** True when the stored PO number already carries a `PO-` / `PO ` prefix. */
export const poIsPrefixed = (order) => /^PO[-\s]/i.test((order && order.po_number) || '');

/**
 * The stored PO number rendered for display — `PO-147` — or `''` when absent.
 * ONE definition of the PO display rule for every PDF surface; a legacy row
 * holding a bare `147` renders `PO-147`, not `147`. `String(raw)` first — the
 * schema admits a number.
 */
export function poDisplayText(order) {
  const raw = order && order.po_number;
  if (raw == null || String(raw).trim() === '') return '';
  const value = String(raw).trim();
  return poIsPrefixed({ po_number: value }) ? value : `PO-${value}`;
}

/**
 * 🔀 **THE FALLBACK WAS `order.reference_number` UNTIL 2026-08-12** — a supplier
 * with no PO received `AURORA-ORD-120.pdf`. Owner: *"we only show the true order
 * ID never a concatinated order ID and production like `AURORA-ORD-120` anywhere
 * JUST ORDER ID AND PO"*. It is now `getOrderNumber(order)` — `ORD-004.pdf`.
 * PO-first is deliberate (how a recipient files a supplier attachment); the
 * order number is on the document itself now, so the filename no longer has to
 * be the only place the order is identifiable.
 */
export function orderPdfFilename(order) {
  const po = poDisplayText(order);
  if (po) return `${po}.pdf`;
  return `${getOrderNumber(order) || 'order'}.pdf`;
}

/**
 * 🔑 **THE ONE-LINE ORDER IDENTITY — `PO-0012 · ORD-004`.**
 *
 * 🔀 **THE ROLE GATE WAS REMOVED AND THE IDENTIFIER CHANGED, 2026-08-12.** It
 * read `(isAdmin || isBuyer) && po ? PO : reference` — so for a buyer/admin the
 * PO REPLACED the reference, and the reference was absent from the title of
 * every PO'd order. Both identifiers must be present for everyone, so this takes
 * no viewer and no role parameter. The order number replaces the reference: the
 * production-prefixed form is shown nowhere.
 *
 * The order number is ALWAYS present; the PO joins it when there is one — no
 * placeholder, no dangling separator.
 */
export function orderIdentityLine(order) {
  const po = poDisplayText(order);
  const number = getOrderNumber(order);
  if (!po) return number;
  if (!number) return po;
  return `${po} · ${number}`;
}

/**
 * The document title's identifier part — **both identifiers, for every viewer.**
 * The second argument (former `{ isAdmin, isBuyer }`) is accepted and ignored,
 * so existing call sites need not change.
 */
export function orderPdfRef(order) {
  return orderIdentityLine(order) || 'Order';
}

/** The full `title` handed to the PDF generator. */
export const orderPdfTitle = (order) => `Order ${orderPdfRef(order)}`;
